@if (label && variant === 'chip') {
  <label class="ptl-autocomplete-search__label">{{ label }}</label>
}

<div
  class="ptl-autocomplete-search__trigger-row"
  [class.ptl-autocomplete-search__trigger-row--filter]="variant !== 'chip'"
  cdkOverlayOrigin
  #origin="cdkOverlayOrigin"
>
  @if (variant !== 'chip') {
    <button
      type="button"
      class="ptl-autocomplete-search__filter-trigger"
      [class.is-menu]="variant === 'menu'"
      [class.is-open]="isOpen"
      [class.is-applied]="variant === 'menu' && isFilterApplied"
      [class.is-disabled]="disabled"
      [disabled]="disabled"
      [attr.aria-label]="triggerAriaLabel"
      [attr.aria-expanded]="isOpen"
      aria-haspopup="listbox"
      (click)="toggleOpen()"
    >
      @if (label && variant === 'filter') {
        <span class="ptl-autocomplete-search__filter-label">{{ label }}:</span>
      }
      <span class="ptl-autocomplete-search__filter-value">{{ filterDisplayValue }}</span>
      <svg
        class="ptl-autocomplete-search__filter-chevron"
        width="11"
        height="11"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <path d="m6 9 6 6 6-6" />
      </svg>
    </button>
  } @else {
    @for (opt of selectedOptions; track opt.value) {
      <div
        class="ptl-autocomplete-search__chip"
        [class.is-disabled]="disabled"
        [style.background-color]="opt.dotColor ? 'color-mix(in srgb, ' + opt.dotColor + ' 15%, transparent)' : null"
        [style.border-color]="opt.dotColor ? 'color-mix(in srgb, ' + opt.dotColor + ' 40%, transparent)' : null"
        [style.color]="opt.dotColor || null"
      >
        <button type="button" class="ptl-autocomplete-search__chip-main" [disabled]="disabled" (click)="toggleOpen()">
          @if (opt.dotColor) {
            <span class="ptl-autocomplete-search__dot" [style.background-color]="opt.dotColor"></span>
          }
          <span class="ptl-autocomplete-search__chip-text">{{ opt.label }}</span>
        </button>
        @if (!disabled) {
          <button
            type="button"
            class="ptl-autocomplete-search__chip-remove"
            [attr.aria-label]="(removeLabel ?? ('translations.global.label.remove' | transloco)) + ' ' + opt.label"
            (click)="removeChip(opt.value, $event)"
          >
            <ptl-icon [icon]="LucideX" size="xs" color="currentColor"></ptl-icon>
          </button>
        }
      </div>
    }

    <button type="button" class="ptl-autocomplete-search__add" [disabled]="disabled" (click)="toggleOpen()">
      {{ addLabel ?? '+ ' + ('translations.global.button.add' | transloco) }}
    </button>
  }
</div>

<ng-template
  cdkConnectedOverlay
  [cdkConnectedOverlayOrigin]="origin"
  [cdkConnectedOverlayOpen]="isOpen"
  [cdkConnectedOverlayPositions]="overlayPositions"
  [cdkConnectedOverlayWidth]="panelWidth"
  [cdkConnectedOverlayPush]="true"
  [cdkConnectedOverlayViewportMargin]="8"
  [cdkConnectedOverlayHasBackdrop]="true"
  cdkConnectedOverlayBackdropClass="cdk-overlay-transparent-backdrop"
  (backdropClick)="close()"
  (detach)="close()"
>
  <div
    class="ptl-autocomplete-search__popover"
    [class.ptl-autocomplete-search__popover--menu]="variant === 'menu'"
    [class.ptl-autocomplete-search__popover--full]="panelFullWidth"
  >
    @if (variant !== 'menu') {
      <div class="ptl-autocomplete-search__search">
        <svg
          class="ptl-autocomplete-search__search-icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          aria-hidden="true"
        >
          <circle cx="11" cy="11" r="7" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          type="text"
          class="ptl-autocomplete-search__search-input"
          [value]="searchText"
          [placeholder]="searchPlaceholder ?? ('translations.global.label.searchPlaceholder' | transloco)"
          [attr.aria-label]="searchAriaLabel || (searchPlaceholder ?? ('translations.global.label.searchPlaceholder' | transloco))"
          (input)="onSearchInput($event)"
        />
      </div>

      <div class="ptl-autocomplete-search__counter">{{ counterLabel }}</div>
    }

    @if (isLoading) {
      <div class="ptl-autocomplete-search__loading">
        <svg class="ptl-autocomplete-search__spinner" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-dasharray="40 20" />
        </svg>
      </div>
    } @else {
      <div class="ptl-autocomplete-search__options">
        @for (opt of filteredOptions; track opt.value) {
          <button
            type="button"
            class="ptl-autocomplete-search__option"
            [class.is-selected]="isSelected(opt.value)"
            [attr.aria-pressed]="isSelected(opt.value)"
            (click)="toggleOption(opt)"
          >
            @if (variant === 'menu') {
              <span class="ptl-autocomplete-search__tick" aria-hidden="true">
                @if (isSelected(opt.value)) {
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                } @else if (opt.dotColor) {
                  <span class="ptl-autocomplete-search__dot" [style.background-color]="opt.dotColor"></span>
                }
              </span>
            } @else {
              <span
                class="ptl-autocomplete-search__indicator"
                [class.ptl-autocomplete-search__indicator--checkbox]="multiSelect"
                [class.ptl-autocomplete-search__indicator--radio]="!multiSelect"
                [class.is-checked]="isSelected(opt.value)"
                aria-hidden="true"
              >
                @if (multiSelect && isSelected(opt.value)) {
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                }
              </span>
              @if (opt.dotColor) {
                <span class="ptl-autocomplete-search__dot" [style.background-color]="opt.dotColor"></span>
              }
            }
            <span class="ptl-autocomplete-search__option-text">
              <span class="ptl-autocomplete-search__option-label">{{ opt.label }}</span>
              @if (opt.sublabel) {
                <span class="ptl-autocomplete-search__option-sublabel">{{ opt.sublabel }}</span>
              }
            </span>
          </button>
        }
      </div>
    }

    @if (creatableTerm) {
      <button type="button" class="ptl-autocomplete-search__create" (click)="onCreate()">
        {{ createLabel ?? '+ ' + ('translations.global.button.create' | transloco) }} "{{ creatableTerm }}"
      </button>
    }

    @if (multiSelect) {
      <div class="ptl-autocomplete-search__footer">
        @if (clearAllLabel) {
          <button type="button" class="ptl-autocomplete-search__clear-all" (click)="onClearAll()">{{ clearAllLabel }}</button>
        } @else {
          <span class="ptl-autocomplete-search__footer-count"
            >{{ pendingSelection.length }}
            {{ selectedCountSuffix ?? ('translations.admin.calendar.filterSelectedSuffix' | transloco) }}</span
          >
        }
        <button type="button" class="ptl-autocomplete-search__done" (click)="onDone()">
          {{ doneLabel ?? ('translations.global.ariaLabels.done' | transloco) }}
        </button>
      </div>
    }
  </div>
</ng-template>
